/**
 * FeedbackDetailPage — read-only view for /dashboard/feedback/:id.
 *
 * Shows full feedback body, attachments, admin reply, timeline (activity
 * log), and a "withdraw" button that calls DELETE when the row is still
 * OPEN. The admin moderation UI is in the admin portal — not here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { DashboardHeader } from '@/src/modules/components/Header';
import { Button } from '@/src/modules/components/Button';
import {
  getMyFeedback,
  withdrawFeedback,
  type ApiFeedback,
  type FeedbackActivity,
} from '@/src/infrastructure/lib/api';
import { FeedbackStatusBadge } from './FeedbackStatusBadge';
import { FeedbackRating } from './FeedbackRating';
import { FEEDBACK_TYPE_OPTIONS, PRIORITY_TONE } from './constants';

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function describe(a: FeedbackActivity): string {
  switch (a.action) {
    case 'CREATED':
      return 'Feedback created';
    case 'STATUS_CHANGED':
      return `Status changed: ${a.metadata.from} → ${a.metadata.to}`;
    case 'PRIORITY_CHANGED':
      return `Priority changed: ${a.metadata.from} → ${a.metadata.to}`;
    case 'RESPONDED':
      return 'Support replied';
    case 'ATTACHMENT_ADDED':
      return 'Attachment added';
    case 'DELETED':
      return 'Feedback removed';
    default:
      return a.action;
  }
}

export function FeedbackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<ApiFeedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    getMyFeedback(id)
      .then(setFeedback)
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load feedback'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleWithdraw() {
    if (!id || !feedback) return;
    if (!window.confirm('Withdraw this feedback? This cannot be undone.')) return;
    setWithdrawing(true);
    try {
      await withdrawFeedback(id);
      navigate('/dashboard/feedback', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not withdraw');
      setWithdrawing(false);
    }
  }

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 max-w-3xl mx-auto space-y-6">
      <DashboardHeader title="Feedback detail" description="Track status, replies, and history." />

      {loading && (
        <div className="space-y-3">
          <div className="h-40 rounded-3xl border border-outline-variant/30 bg-surface-lowest animate-pulse" />
          <div className="h-32 rounded-2xl border border-outline-variant/30 bg-surface-lowest animate-pulse" />
        </div>
      )}

      {!loading && error && (
        <div role="alert" className="rounded-xl border border-rose-500/30 bg-rose-50/60 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && feedback && (
        <motion.article
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          <section className="bg-surface-lowest rounded-3xl border border-outline-variant/30 shadow-sm p-6 md:p-8 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs text-on-surface-variant uppercase tracking-wide">
                  <span className="font-semibold">
                    {FEEDBACK_TYPE_OPTIONS.find((o) => o.value === feedback.type)?.label ?? feedback.type}
                  </span>
                  <span>·</span>
                  <span className={PRIORITY_TONE[feedback.priority]}>{feedback.priority}</span>
                  <span>·</span>
                  <span>{formatDateTime(feedback.createdAt)}</span>
                </div>
                <h1 className="mt-1 text-xl font-bold text-on-surface">{feedback.title}</h1>
              </div>
              <FeedbackStatusBadge status={feedback.status} />
            </div>

            <p className="whitespace-pre-wrap text-sm text-on-surface leading-relaxed">
              {feedback.description}
            </p>

            {feedback.rating !== null && (
              <div>
                <FeedbackRating value={feedback.rating} readOnly size="sm" />
              </div>
            )}

            {feedback.attachments.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {feedback.attachments.map((a) => (
                  <a
                    key={a.id}
                    href={a.fileUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block aspect-square rounded-xl border border-outline-variant/40 overflow-hidden bg-surface hover:border-primary/40 transition-colors"
                  >
                    {a.fileType.startsWith('image/') ? (
                      <img src={a.fileUrl} alt="Attachment" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-xs text-on-surface-variant gap-1">
                        <span className="text-2xl">📎</span>
                        <span>{a.fileType.split('/').pop()?.toUpperCase()}</span>
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}

            {feedback.status === 'OPEN' && (
              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  onClick={handleWithdraw}
                  disabled={withdrawing}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-50"
                >
                  {withdrawing ? 'Withdrawing…' : 'Withdraw this feedback'}
                </button>
              </div>
            )}
          </section>

          {feedback.adminResponse && (
            <section className="rounded-3xl border border-primary/20 bg-primary/5 p-6">
              <h2 className="text-sm font-bold text-primary uppercase tracking-wide">
                Reply from support
              </h2>
              <p className="mt-2 whitespace-pre-wrap text-sm text-on-surface">
                {feedback.adminResponse}
              </p>
            </section>
          )}

          {feedback.activity && feedback.activity.length > 0 && (
            <section className="rounded-3xl border border-outline-variant/30 bg-surface-lowest p-6">
              <h2 className="text-sm font-bold text-on-surface">Activity</h2>
              <ol className="mt-3 space-y-3">
                {feedback.activity.map((a) => (
                  <li key={a.id} className="flex items-start gap-3 text-sm">
                    <span className="mt-1 h-2 w-2 rounded-full bg-primary/60 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-on-surface">{describe(a)}</div>
                      <div className="text-xs text-on-surface-variant">{formatDateTime(a.createdAt)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <div>
            <Button onClick={() => navigate('/dashboard/feedback')} variant="ghost">
              ← Back to feedback
            </Button>
          </div>
        </motion.article>
      )}
    </div>
  );
}
